import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { Helmet } from 'react-helmet';

const HeaderContainer = styled.header`
  background-color: white;
  box-shadow: var(--shadow);
  position: sticky;
  top: 0;
  z-index: 1000;
  transition: transform 0.3s ease-in-out;
  transform: ${({ isHidden }) => (isHidden ? 'translateY(-100%)' : 'translateY(0)')};
`;

const Navbar = styled.nav`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 0;

  @media (max-width: 768px) {
    flex-wrap: wrap;
  }
`;

const Logo = styled(Link)`
  text-decoration: none;
  display: inline-block;

  img {
    vertical-align: middle;
    width: 290px;
    height: 82px;

    @media (max-width: 768px) {
      max-width: 100%;
      height: auto;
    }
  }
`;

const MobileMenuButton = styled.button`
  display: none;
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: var(--text-color);
  padding: 5px;
  margin-left: auto;
  
  @media (max-width: 768px) {
    display: block;
  }
`;

const NavMenu = styled.ul`
  display: flex;
  list-style: none;
  font-size: 21px;
  margin: 0;
  padding: 0;

  @media (max-width: 768px) {
    display: ${({ isOpen }) => (isOpen ? 'flex' : 'none')};
    flex-direction: column;
    width: 100%;
    margin-top: 15px;
    align-items: center;
    font-size: 17px;
  }

  li {
    margin-left: 20px;

    @media (max-width: 768px) {
      margin: 10px 0;
    }
  }
`;

const NavLink = styled(Link)`
  text-decoration: none;
  color: var(--text-color);
  font-weight: 500;
  transition: color 0.3s;
  padding: 7px 10px;
  white-space: nowrap;
  
  &.active {
    color: var(--primary-color);
    font-weight: 700;
  }

  &:hover {
    color: var(--primary-color);
  }
`;

const NavExternalLink = styled.a`
  text-decoration: none;
  color: var(--text-color);
  font-weight: 500;
  transition: color 0.3s;
  padding: 7px 10px;
  white-space: nowrap;
  cursor: pointer;
  
  &.active {
    color: var(--primary-color);
    font-weight: 700;
  }

  &:hover {
    color: var(--primary-color);
  }
`;

function Header() {
  const [isHidden, setIsHidden] = useState(false);
  const [lastScrollY, setLastScrollY] = useState(0);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  // Optimize scroll performance with debounce
  useEffect(() => {
    let timeoutId;
    
    const handleScroll = () => {
      clearTimeout(timeoutId);
      
      timeoutId = setTimeout(() => {
        const currentScrollY = window.scrollY;

        if (currentScrollY > lastScrollY && currentScrollY > 100) {
          setIsHidden(true);
        } else {
          setIsHidden(false);
        }

        setLastScrollY(currentScrollY);
      }, 10);
    };

    window.addEventListener('scroll', handleScroll);
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
      clearTimeout(timeoutId);
    };
  }, [lastScrollY]);

  // Handle hash navigation effect
  useEffect(() => {
    if (location.hash) {
      // Give the DOM time to fully render before attempting to scroll
      setTimeout(() => {
        const id = location.hash.replace('#', '');
        const element = document.getElementById(id);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
    }
  }, [location]);

  // Close mobile menu when changing routes
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  // Custom navigation function to handle home section navigation
  const handleSectionNavigation = (e, sectionId) => {
    e.preventDefault();
    
    const isHomePage = location.pathname === '/' || location.pathname === '/index';
    
    if (isHomePage) {
      // If already on home page, just scroll to section
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    } else {
      // If on another page, navigate to home first with the hash
      navigate(`/#${sectionId}`);
    }
  };

  // Schema.org structured data for navigation
  const navigationSchema = {
    "@context": "https://schema.org",
    "@type": "SiteNavigationElement",
    "name": [
      "Taxi & Mietwagenservice",
      "Sicherheit",
      "Reinigung", 
      "Jobs",
      "Über uns",
      "Kontakt"
    ],
    "url": [
      "https://guardiansmoves.de/taxi",
      "https://guardiansmoves.de/sicherheit",
      "https://guardiansmoves.de/reinigung",
      "https://guardiansmoves.de/jobs",
      "https://guardiansmoves.de/#ueber-uns",
      "https://guardiansmoves.de/#kontakt"
    ]
  };

  // Logo schema
  const logoSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "url": "https://guardiansmoves.de",
    "logo": "https://guardiansmoves.de/images/logo.png"
  };

  // Generate breadcrumb based on current location
  const getBreadcrumbSchema = () => {
    const path = location.pathname;
    let breadcrumbItems = [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": "https://guardiansmoves.de"
      }
    ];
    
    if (path === '/sicherheit') {
      breadcrumbItems.push({
        "@type": "ListItem",
        "position": 2,
        "name": "Sicherheit",
        "item": "https://guardiansmoves.de/sicherheit"
      });
    } else if (path === '/taxi') {
      breadcrumbItems.push({
        "@type": "ListItem",
        "position": 2,
        "name": "Taxi & Mietwagenservice",
        "item": "https://guardiansmoves.de/taxi"
      });
    } else if (path === '/reinigung') {
      breadcrumbItems.push({
        "@type": "ListItem",
        "position": 2,
        "name": "Reinigung",
        "item": "https://guardiansmoves.de/reinigung"
      });
    } else if (path === '/jobs') {
      breadcrumbItems.push({
        "@type": "ListItem",
        "position": 2,
        "name": "Jobs",
        "item": "https://guardiansmoves.de/jobs"
      });
    }
    
    return {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": breadcrumbItems
    };
  };

  return (
    <>
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(navigationSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(logoSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(getBreadcrumbSchema())}
        </script>
      </Helmet>
      
      <HeaderContainer isHidden={isHidden}>
        <div className="container">
          <Navbar className="navbar">
            <Logo to="/" className="logo" aria-label="GuardiansMoves Homepage">
              <img src="/images/logo.png" alt="GuardiansMoves Logo" width="290" height="82" />
            </Logo>
            
            <MobileMenuButton 
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-expanded={mobileMenuOpen}
              aria-controls="nav-menu"
              aria-label={mobileMenuOpen ? "Close menu" : "Open menu"}
            >
              {mobileMenuOpen ? '✕' : '☰'}
            </MobileMenuButton>
            
            <NavMenu 
              className="nav-menu" 
              id="nav-menu"
              isOpen={mobileMenuOpen}
              role="menubar" 
              aria-label="Main Navigation"
            >
              <li role="none">
                <NavLink 
                  to="/taxi" 
                  role="menuitem"
                  className={location.pathname === '/taxi' ? 'active' : ''}
                >
                  Taxi&Mietwagenservice
                </NavLink>
              </li>
              <li role="none">
                <NavLink 
                  to="/sicherheit" 
                  role="menuitem"
                  className={location.pathname === '/sicherheit' ? 'active' : ''}
                >
                  Sicherheit
                </NavLink>
              </li>
              <li role="none">
                <NavLink 
                  to="/reinigung" 
                  role="menuitem"
                  className={location.pathname === '/reinigung' ? 'active' : ''}
                >
                  Reinigung
                </NavLink>
              </li>
              <li role="none">
                <NavLink 
                  to="/jobs" 
                  role="menuitem"
                  className={location.pathname === '/jobs' ? 'active' : ''}
                >
                  Jobs
                </NavLink>
              </li>
              <li role="none">
                <NavExternalLink 
                  href="/#ueber-uns" 
                  role="menuitem"
                  onClick={(e) => handleSectionNavigation(e, 'ueber-uns')}
                  className={location.hash === '#ueber-uns' ? 'active' : ''}
                >
                  Über uns
                </NavExternalLink>
              </li>
              <li role="none">
                <NavExternalLink 
                  href="/#kontakt" 
                  role="menuitem"
                  onClick={(e) => handleSectionNavigation(e, 'kontakt')}
                  className={location.hash === '#kontakt' ? 'active' : ''}
                >
                  Kontakt
                </NavExternalLink>
              </li>
            </NavMenu>
          </Navbar>
        </div>
      </HeaderContainer>
    </>
  );
}

export default Header;